import { secureRandom } from '@/lib/secureRandom';
import { create } from 'zustand';
import apiClient from '../lib/api';

export type ProjectStatus = 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
export type ProjectType = 'fixed-price' | 'time-materials' | 'retainer' | 'internal';
export type IndustryTemplate = 'it-services' | 'manufacturing' | 'retail' | 'logistics' | 'ites' | 'custom';
export type HealthStatus = 'green' | 'yellow' | 'red';

export interface Milestone {
  id: string;
  name: string;
  dueDate: string;
  status: 'pending' | 'in-progress' | 'completed' | 'missed';
  deliverables: string[];
}

export interface AuditEntry {
  id: string;
  action: string;
  actor: string;
  module: string;
  timestamp: string;
  details: string;
  type: 'creation' | 'update' | 'milestone' | 'member';
}

export interface ProjectMember {
  id: string;
  userId: string;
  name: string;
  role: string;
  avatar?: string;
  allocation: number; // 0-100
}

export interface Project {
  id: string;
  code: string;
  name: string;
  description?: string;
  status: ProjectStatus;
  type: ProjectType;
  industryTemplate?: IndustryTemplate;

  // Client
  clientId?: string;
  clientName?: string;

  // Financials
  budget?: number;
  currency: string;
  billingType: 'fixed' | 'hourly' | 'monthly';
  hourlyRate?: number;

  // Timeline
  startDate: string;
  endDate?: string;
  milestones: Milestone[];

  // Health & Ownership
  healthStatus: HealthStatus;
  ownerId: string;
  ownerName?: string;
  members: ProjectMember[];

  // Metrics
  progress: number; // 0-100
  tasksCount: {
    total: number;
    completed: number;
  };

  // Timestamps
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

function normalizeProject(raw: any): Project {
  const startDate = raw?.startDate ?? raw?.start_date ?? new Date().toISOString().split('T')[0];
  const endDate = raw?.endDate ?? raw?.end_date ?? undefined;

  return {
    id: raw?.id ?? '',
    code: raw?.code ?? '',
    name: raw?.name ?? '',
    description: raw?.description ?? undefined,
    status: (raw?.status ?? 'planning') as ProjectStatus,
    type: (raw?.type ?? 'internal') as ProjectType,
    industryTemplate: raw?.industryTemplate ?? raw?.industry_template ?? undefined,
    clientId: raw?.clientId ?? raw?.client_id ?? undefined,
    clientName: raw?.clientName ?? raw?.client_name ?? undefined,
    budget: raw?.budget ?? undefined,
    currency: raw?.currency ?? 'USD',
    billingType: (raw?.billingType ?? raw?.billing_type ?? 'fixed') as Project['billingType'],
    hourlyRate: raw?.hourlyRate ?? raw?.hourly_rate ?? undefined,
    startDate,
    endDate,
    milestones: Array.isArray(raw?.milestones) ? raw.milestones : [],
    healthStatus: (raw?.healthStatus ?? raw?.health_status ?? 'green') as HealthStatus,
    ownerId: raw?.ownerId ?? raw?.owner_id ?? '',
    ownerName: raw?.ownerName ?? raw?.owner_name ?? undefined,
    members: Array.isArray(raw?.members) ? raw.members : [],
    progress: Number.isFinite(raw?.progress) ? raw.progress : 0,
    tasksCount:
      raw?.tasksCount && typeof raw.tasksCount === 'object'
        ? raw.tasksCount
        : { total: 0, completed: 0 },
    createdAt: raw?.createdAt ?? raw?.created_at ?? new Date().toISOString(),
    updatedAt: raw?.updatedAt ?? raw?.updated_at ?? new Date().toISOString(),
    createdBy: raw?.createdBy ?? raw?.created_by ?? '',
  };
}

function normalizeProjectMember(raw: any): ProjectMember {
  return {
    id: raw?.id ?? '',
    userId: raw?.userId ?? raw?.user_id ?? '',
    name: raw?.name ?? raw?.email ?? 'Member',
    role: raw?.role ?? 'member',
    avatar: raw?.avatar ?? raw?.avatar_url ?? undefined,
    allocation: Number.isFinite(raw?.allocation) ? raw.allocation : 100,
  };
}

const SAMPLE_PROJECTS: Project[] = [];

interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  auditTrail: AuditEntry[];
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };

  fetchProjects: (filters?: any) => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  fetchAuditTrail: (projectId: string) => Promise<void>;
  createProject: (data: Partial<Project>) => Promise<void>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  addProjectMember: (projectId: string, member: Omit<ProjectMember, 'id'>) => Promise<void>;
  removeProjectMember: (projectId: string, memberId: string) => Promise<void>;
  searchUsers: (search: string) => Promise<any[]>;
  addMilestone: (projectId: string, milestone: Partial<Milestone>) => Promise<void>;
  updateMilestone: (projectId: string, milestoneId: string, data: Partial<Milestone>) => Promise<void>;
  deleteMilestone: (projectId: string, milestoneId: string) => Promise<void>;
  clearCurrentProject: () => void;
  clearError: () => void;
}

export const useProjectStore = create<ProjectState>((set) => ({
  projects: [],
  currentProject: null,
  auditTrail: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },

  fetchProjects: async (filters?: any) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.get('/work/projects', { params: filters });
      const { data, pagination } = response.data;

      set({
        projects: Array.isArray(data) ? data.map(normalizeProject) : [],
        pagination: pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
        isLoading: false,
      });
    } catch (error: any) {
      console.warn('API fetch projects failed:', error.message);
      set({
        projects: [],
        pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
        error: null,
        isLoading: false,
      });
    }
  },

  fetchProject: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      const [projectResponse, membersResponse, taskStatsResponse] = await Promise.all([
        apiClient.get(`/work/projects/${id}`),
        apiClient.get(`/work/projects/${id}/members`),
        apiClient.get(`/work/tasks/stats`, { params: { projectId: id } }),
      ]);

      const baseProject = normalizeProject(projectResponse.data);
      const members = Array.isArray(membersResponse.data)
        ? membersResponse.data.map(normalizeProjectMember)
        : [];

      const stats = taskStatsResponse.data || {};
      const totalTasks = Number(stats.total) || 0;
      const doneTasks = Number(stats.byStatus?.done) || 0;
      const progress = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

      set({
        currentProject: {
          ...baseProject,
          members,
          tasksCount: { total: totalTasks, completed: doneTasks },
          progress,
          milestones: baseProject.milestones || [],
        },
        isLoading: false,
      });
    } catch (error: any) {
      console.warn('API fetch project failed:', error.message);
      const sample = SAMPLE_PROJECTS.find(p => p.id === id);
      set({
        currentProject: sample || null,
        error: sample ? null : 'Project not found',
        isLoading: false,
      });
    }
  },

  fetchAuditTrail: async (projectId: string) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.get(`/work/projects/${projectId}/audit`);
      const rows = Array.isArray(response.data) ? response.data : [];
      const auditTrail: AuditEntry[] = rows.map((row: any) => {
        const actor = row?.actor_name || row?.actor_email || row?.user_id || 'System';
        const details =
          typeof row?.changes === 'string'
            ? row.changes
            : row?.changes
              ? JSON.stringify(row.changes)
              : '';
        const action = row?.action ?? 'audit';
        const type: AuditEntry['type'] =
          typeof action === 'string' && action.includes('create')
            ? 'creation'
            : typeof action === 'string' && action.includes('member')
              ? 'member'
              : 'update';

        return {
          id: row?.id ?? crypto.randomUUID(),
          action,
          actor,
          module: 'Projects',
          timestamp: row?.created_at ?? new Date().toISOString(),
          details,
          type,
        };
      });
      set({ auditTrail, isLoading: false });
    } catch (error) {
      set({ auditTrail: [], isLoading: false });
    }
  },

  createProject: async (data: Partial<Project>) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.post('/work/projects', data);
      const newProject = normalizeProject(response.data);

      set((state) => ({
        projects: [newProject, ...state.projects],
        currentProject: newProject,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to create project',
        isLoading: false,
      });
      throw error;
    }
  },

  updateProject: async (id: string, data: Partial<Project>) => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiClient.patch(`/work/projects/${id}`, data);
      const updatedProject = normalizeProject(response.data);

      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? updatedProject : p)),
        currentProject: updatedProject,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to update project',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteProject: async (id: string) => {
    set({ isLoading: true, error: null });

    try {
      await apiClient.delete(`/work/projects/${id}`);

      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
        isLoading: false,
      }));
    } catch (error: any) {
      set({
        error: error.response?.data?.message || 'Failed to delete project',
        isLoading: false,
      });
      throw error;
    }
  },

  addProjectMember: async (projectId: string, member: Omit<ProjectMember, 'id'>) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post(`/work/projects/${projectId}/members`, {
        memberId: member.userId,
        role: member.role,
      });
      const newMember = normalizeProjectMember(response.data);
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          members: [...state.currentProject.members, newMember]
        } : null,
        isLoading: false
      }));
    } catch (error) {
      // Simulation for development
      const newMember = { ...member, id: secureRandom().toString(36).substr(2, 9) };
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          members: [...state.currentProject.members, newMember]
        } : null,
        isLoading: false
      }));
    }
  },

  removeProjectMember: async (projectId: string, memberId: string) => {
    try {
      await apiClient.delete(`/work/projects/${projectId}/members/${memberId}`);
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          members: state.currentProject.members.filter(m => m.id !== memberId)
        } : null
      }));
    } catch (error) {
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          members: state.currentProject.members.filter(m => m.id !== memberId)
        } : null
      }));
    }
  },

  searchUsers: async (search: string) => {
    try {
      const response = await apiClient.get('/work/projects/users/search', { params: { search } });
      return response.data;
    } catch (error) {
      console.error('Failed to search users:', error);
      return [];
    }
  },

  addMilestone: async (projectId: string, milestone: Partial<Milestone>) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post(`/work/projects/${projectId}/milestones`, milestone);
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          milestones: [...(state.currentProject.milestones || []), response.data]
        } : null,
        isLoading: false
      }));
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  updateMilestone: async (projectId: string, milestoneId: string, data: Partial<Milestone>) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.patch(`/work/projects/${projectId}/milestones/${milestoneId}`, data);
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          milestones: state.currentProject.milestones.map(m => m.id === milestoneId ? response.data : m)
        } : null,
        isLoading: false
      }));
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  deleteMilestone: async (projectId: string, milestoneId: string) => {
    set({ isLoading: true });
    try {
      await apiClient.delete(`/work/projects/${projectId}/milestones/${milestoneId}`);
      set(state => ({
        currentProject: state.currentProject ? {
          ...state.currentProject,
          milestones: state.currentProject.milestones.filter(m => m.id !== milestoneId)
        } : null,
        isLoading: false
      }));
    } catch (error) {
      set({ isLoading: false });
      throw error;
    }
  },

  clearCurrentProject: () => {
    set({ currentProject: null });
  },

  clearError: () => {
    set({ error: null });
  },
}));
